import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { authApi, AuthLoginRes, Organization, User } from 'store/apis/auth.api';
import { canOrganizationCreateBuildings } from 'consts/user.const';
import { EPaymentMethod, EUserRole } from 'enums';
import { FetchUserRes, userApi } from 'store/apis/user.api';
import { getImageRefreshed } from 'utils';

import { RootState } from '..';

type AuthSliceState = {
  user: User | null;
  employeeCode: string | null;
};

/**
 * Slice
 */
export const authSlice = createSlice({
  name: 'authSlice',
  initialState: {
    user: null,
    employeeCode: null,
  } as AuthSliceState,
  reducers: {
    logout: (state) => {
      state.user = null;
    },
    setEmployeeCode: (state, action: PayloadAction<string>) => {
      state.employeeCode = action.payload;
    },
  },
  extraReducers: (builder) => {
    /**
     * Resolve image cache
     */
    const getUpdatedUserInfo = (user: any) => {
      let newUser = { ...user };
      let default_organization = {
        ...newUser.default_organization,
      };

      default_organization = {
        ...default_organization,
        logo: getImageRefreshed(default_organization?.logo),
      };

      newUser = {
        ...newUser,
        profile_photo: getImageRefreshed(newUser?.profile_photo),
        default_organization,
      };

      return newUser;
    };

    return builder
      .addMatcher(authApi.endpoints.authLogin.matchFulfilled, (state, { payload }: PayloadAction<AuthLoginRes>) => {
        state.user = getUpdatedUserInfo(payload.user);
      })
      .addMatcher(userApi.endpoints.fetchUser.matchFulfilled, (state, { payload }: PayloadAction<FetchUserRes>) => {
        state.user = getUpdatedUserInfo(payload);
      });
  },
});

/**
 * Actions
 */
export const { logout, setEmployeeCode } = authSlice.actions;

/**
 * Selectors
 */
export const selectAuthSlice = (state: RootState) => state.authSlice;
export const selectEmployeeCode = createSelector([selectAuthSlice], (authSlice) => authSlice.employeeCode);
export const selectCurrentUser = createSelector([selectAuthSlice], (authSlice) => authSlice.user);
/**
 * Use this selector for getting any user info needed
 */
export const selectCurrentUserInfo = createSelector([selectCurrentUser], (user) => {
  const id = user?.id || -1;
  const name = user?.name || '';
  const title = user?.title || '';
  const isLoggedIn = !!user;
  const email = user?.email || '';
  const emailVerified = !!user?.email_verified_at;

  /**
   * Organization
   */
  const org = user?.default_organization || null;
  const { slug: orgSlug = '', name: orgName = '' } = org || ({} as Organization);
  const canCreateBuildings = canOrganizationCreateBuildings(org?.id);

  const userRoles = user?.roles || [];
  const isAdmin = userRoles.findIndex((role) => role.name === EUserRole.ADMIN) > -1;
  const primaryBrandColor = user?.default_organization.primary_brand_color || undefined;
  const billingAddress = user?.default_organization?.organization_billing?.billing_address;
  const shippingAddress = user?.default_organization?.organization_billing?.shipping_address;
  const paymentMethod = user?.default_organization?.organization_billing?.pm_type || EPaymentMethod.CARD;
  const nameOnPayment = user?.default_organization?.organization_billing?.name || '';
  const billingEmail = user?.default_organization?.organization_billing?.email || '';
  const cardLastFourCode = user?.default_organization?.organization_billing?.pm_last_four || '';
  // TODO: should be refactored
  const cartPaymentMethod = {
    pmType: paymentMethod,
    pmLastFour: cardLastFourCode,
    name: nameOnPayment,
    email: billingEmail,
  };
  const avatarUrl = user?.profile_photo?.url || '';
  const orgLogoUrl = user?.default_organization?.logo?.url || '';

  return {
    id,
    name,
    title,
    isLoggedIn,
    email,
    emailVerified,
    org,
    orgSlug,
    orgName,
    canCreateBuildings,
    userRoles,
    isAdmin,
    primaryBrandColor,
    billingAddress,
    shippingAddress,
    paymentMethod,
    nameOnPayment,
    billingEmail,
    cardLastFourCode,
    cartPaymentMethod,
    avatarUrl,
    orgLogoUrl,
  };
});
